import { useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Search, ChevronRight, Bell, Gauge, Store, Flower2, Users, Wallet, Settings, Menu, X } from "lucide-react";
import { api, errText, mfaTrust } from "../../lib/api";
import { ForgotPasswordLink } from "../../components/ForgotPasswordLink";
import Overview from "./Overview";
import OrdersPage from "./OrdersPage";
import FloristsPage from "./FloristsPage";
import FloristProfile from "./FloristProfile";
import CustomersPage from "./CustomersPage";
import CustomerProfile from "./CustomerProfile";
import PaymentsPage from "./PaymentsPage";
import SubscriptionsPage from "./SubscriptionsPage";
import FinancePage from "./FinancePage";
import ApplicationsPage from "./ApplicationsPage";
import CataloguePage from "./CataloguePage";
import ReviewsPage from "./ReviewsPage";
import MessagesPage from "./MessagesPage";
import ActivityPage from "./ActivityPage";
import SettingsPage from "./SettingsPage";
import PlansPage from "./PlansPage";
import AddonsModerationPage from "./AddonsModerationPage";
import TeamPage from "./TeamPage";
import PayoutRequestsPage from "./PayoutRequestsPage";
import DeliveryNetworkPage from "./DeliveryNetworkPage";
import PartnersPage from "./PartnersPage";
import CityPagesPage from "./CityPagesPage";
import OccasionPagesPage from "./OccasionPagesPage";
import DiscountsPage from "./DiscountsPage";
import ExtrasPage from "./ExtrasPage";
import OpsContactsPage from "./OpsContactsPage";
import NewsletterPage from "./NewsletterPage";
import EmailTemplatesPage from "./EmailTemplatesPage";
import AgenticCommercePage from "./AgenticCommercePage";
import MarketingPage from "./MarketingPage";
import { MfaChallenge, MfaEnroll, SecurityPanel } from "../../components/Mfa";

// [group_key, group_label, group_icon, [ [key, label, path, required_perm] ]]
const NAV_SPEC = [
  ["operations", "Operations", Gauge, [
    ["overview", "Overview", "/admin", null],
    ["orders", "Orders", "/admin/orders", "view_orders"],
    ["live", "Live operations", "/admin/orders?scope=live", "view_orders"],
    ["at-risk", "At-risk queue", "/admin/orders?scope=at_risk", "view_orders"],
    ["subscriptions", "Recurring plans", "/admin/subscriptions", "view_orders"],
  ]],
  ["partners", "Partners", Store, [
    ["partners", "Partner network", "/admin/partners", "view_florists"],
    ["florists", "All shops", "/admin/florists", "view_florists"],
    ["delivery-network", "Delivery network", "/admin/delivery-network", "view_florists"],
  ]],
  ["catalogue", "Catalogue", Flower2, [
    ["catalogue", "Bouquets & prices", "/admin/catalogue", "moderate_catalogue"],
    ["city-pages", "City pages · local SEO", "/admin/city-pages", "moderate_catalogue"],
    ["occasion-pages", "Occasion pages · local SEO", "/admin/occasion-pages", "moderate_catalogue"],
    ["discounts", "Discounts & coupons", "/admin/discounts", "manage_settings"],
    ["extras", "Extras & add-ons", "/admin/extras", "manage_settings"],
    ["reviews", "Reviews", "/admin/reviews", "moderate_reviews"],
  ]],
  ["customers", "Customers", Users, [
    ["customers", "Customer list", "/admin/customers", "view_customers"],
    ["notifications", "Messages sent", "/admin/messages", "view_messages"],
  ]],
  ["finance", "Finance", Wallet, [
    ["payments", "Payments", "/admin/payments", "view_finance"],
    ["payout-requests", "Payout requests", "/admin/payout-requests", "approve_payout_requests"],
    ["finance", "Finance & payouts", "/admin/finance", "view_finance"],
  ]],
  ["settings", "Settings", Settings, [
    ["settings", "Platform settings", "/admin/settings", "manage_settings"],
    ["team", "Team & roles", "/admin/team", "manage_team"],
    ["ops-contacts", "Alert contacts", "/admin/ops-contacts", "manage_settings"],
    ["newsletter", "Newsletter · Acumbamail", "/admin/newsletter", "manage_settings"],
    ["email-templates", "E-mail templates", "/admin/email-templates", "manage_settings"],
    ["agentic", "Agentic commerce", "/admin/agentic", "manage_settings"],
    ["marketing", "Marketing & launch", "/admin/marketing", "manage_settings"],
    ["activity", "Admin activity", "/admin/activity", "view_activity"],
    ["security", "My security", "/admin/security", null],
  ]],
];

// Picks the single most specific nav entry matching the current URL, so that
// query-scoped siblings (Orders vs Live operations) never both light up.
const activeKey = (pathname, search) => {
  const cur = new URLSearchParams(search);
  let best = null;
  let bestScore = -1;
  for (const [, , , items] of NAV_SPEC) {
    for (const [key, , to] of items) {
      const [path, query] = to.split("?");
      const pathHit = path === "/admin" ? pathname === "/admin" : pathname === path || pathname.startsWith(`${path}/`);
      if (!pathHit) continue;
      const params = [...new URLSearchParams(query || "")];
      if (!params.every(([k, v]) => cur.get(k) === v)) continue;
      const score = path.length + params.length * 100;
      if (score > bestScore) { bestScore = score; best = key; }
    }
  }
  return best;
};

const SecurityPage = () => {
  const [status, setStatus] = useState(null);
  const load = () => api.get("/auth/mfa/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-security-page">
      <h1 className="font-display text-2xl text-ink mb-1">My security</h1>
      <p className="text-sm text-muted mb-6">Change your password and manage two-step verification.</p>
      {status ? <SecurityPanel status={status} onStatusChange={load} /> : null}
    </div>
  );
};

const Shell = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const perms = useMemo(() => user.user?.admin_permissions || [], [user]);
  const can = (p) => !p || perms.includes(p);
  const nav = useMemo(() => NAV_SPEC
    .map(([key, label, Icon, items]) => [key, label, Icon, items.filter(([, , , p]) => can(p))])
    .filter(([, , , items]) => items.length > 0), [perms]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = activeKey(location.pathname, location.search);
  const groupOfKey = (k) => (NAV_SPEC.find(([, , , items]) => items.some(([key]) => key === k)) || [])[0] || null;
  const [openGroup, setOpenGroup] = useState(() => groupOfKey(activeKey(location.pathname, location.search)) || (nav[0] ? nav[0][0] : null));
  useEffect(() => {
    const g = groupOfKey(activeKey(location.pathname, location.search));
    if (g) setOpenGroup(g);
  }, [location.pathname, location.search]);

  const [slaCount, setSlaCount] = useState(0);
  const [appCount, setAppCount] = useState(0);
  const [ops, setOps] = useState({ count: 0 });
  useEffect(() => {
    if (!perms.includes("view_florists")) return;
    const load = () => {
      api.get("/admin/sla-alerts").then((r) => setSlaCount(r.data.count)).catch(() => {});
      api.get("/admin/applications/processing-count").then((r) => setAppCount(r.data.count)).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [perms]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!perms.includes("view_orders")) return;
    const load = () => api.get("/admin/ops-queue").then((r) => setOps(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [perms]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = ops.count > 0 ? `(${ops.count}) Fleurinelle · Ops` : "Fleurinelle · Ops";
    return () => { document.title = "Fleurinelle"; };
  }, [ops.count]);

  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [location.pathname, location.search]);

  const badge = (key) => (key === "delivery-network" ? slaCount : key === "applications" ? appCount : key === "at-risk" ? ops.count : 0);
  const badgeTestid = (key) => (key === "delivery-network" ? "admin-sla-badge" : key === "applications" ? "admin-applications-badge" : `admin-nav-badge-${key}`);

  const navTree = (
    <>
      {nav.map(([gkey, glabel, Icon, items]) => {
        const open = openGroup === gkey;
        const groupCount = items.reduce((n, [k]) => n + (badge(k) || 0), 0);
        return (
          <div key={gkey} className="px-2 mb-0.5">
            <button data-testid={`admin-navgroup-${gkey}`} aria-expanded={open}
                    onClick={() => setOpenGroup(open ? null : gkey)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm transition-colors duration-200 ${open ? "bg-plaster/10 text-plaster" : "text-plaster/70 hover:text-plaster hover:bg-plaster/5"}`}>
              <Icon size={16} className={open ? "text-brass" : "text-plaster/50"} />
              <span className="font-medium tracking-wide">{glabel}</span>
              {!open && groupCount > 0 ? (
                <span className="ms-auto bg-danger text-plaster text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">{groupCount > 99 ? "99+" : groupCount}</span>
              ) : null}
              <ChevronRight size={14} className={`${groupCount > 0 && !open ? "ms-1" : "ms-auto"} text-plaster/40 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="ms-[26px] my-1 border-s border-plaster/15 ps-2 space-y-px">
                {items.map(([key, label, to]) => {
                  const isActive = current === key;
                  const count = badge(key);
                  return (
                    <Link key={key} to={to} data-testid={`admin-nav-${key}`} onClick={() => setNavOpen(false)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-[13px] transition-colors duration-150 ${isActive ? "bg-brass/20 text-plaster font-medium" : "text-plaster/60 hover:text-plaster hover:bg-plaster/5"}`}>
                      <span className={`w-1 h-1 rounded-full shrink-0 ${isActive ? "bg-brass" : "bg-transparent"}`} />
                      <span className="truncate">{label}</span>
                      {count > 0 ? (
                        <span data-testid={badgeTestid(key)}
                              className="ms-auto bg-danger text-plaster text-[10px] font-medium rounded-full px-1.5 min-w-[18px] text-center">{count > 99 ? "99+" : count}</span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-plaster" data-testid="admin-dashboard">
      <header className="bg-ink text-plaster px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
        <button type="button" data-testid="admin-nav-toggle" onClick={() => setNavOpen(true)}
                className="lg:hidden p-1.5 -ms-1 text-plaster/80 hover:text-plaster" aria-label="Menu">
          <Menu size={20} />
        </button>
        <img src="/brand/fleurinelle-header-dark.png" alt="Fleurinelle" width="1200" height="360" className="h-6 sm:h-7 w-auto block select-none" draggable="false" />
        <span className="text-xs text-plaster/70 hidden sm:inline">operations</span>
        {can("view_orders") ? (
          <form className="relative ms-6 hidden md:block" onSubmit={(e) => { e.preventDefault(); navigate(`/admin/orders?q=${encodeURIComponent(q)}`); }}>
            <Search size={14} className="absolute start-2.5 top-1/2 -translate-y-1/2 text-plaster/50" />
            <input className="bg-plaster/10 border border-plaster/20 text-plaster placeholder:text-plaster/40 text-sm ps-8 pe-3 py-1.5 w-56 xl:w-72 focus:outline-none focus:border-brass"
                   placeholder="Find an order, email, recipient…" value={q} data-testid="admin-global-search" onChange={(e) => setQ(e.target.value)} />
          </form>
        ) : null}
        {can("view_orders") ? (
          <button type="button" data-testid="admin-ops-bell" onClick={() => navigate("/admin/orders?scope=at_risk")}
                  title={ops.count > 0
                    ? `${ops.escalated || 0} escalated · ${ops.unassigned || 0} without a shop · ${ops.issues || 0} buyer issues${ops.oldest ? ` · oldest ${ops.oldest.order_number}` : ""}`
                    : "No orders waiting on operations"}
                  className={`relative ms-auto p-2 rounded-full transition-colors ${ops.count > 0 ? "text-plaster hover:bg-plaster/10" : "text-plaster/50 hover:text-plaster"}`}>
            <Bell size={18} className={ops.count > 0 ? "animate-[pulse_2s_ease-in-out_infinite]" : ""} />
            {ops.count > 0 ? (
              <span data-testid="admin-ops-count"
                    className="absolute -top-0.5 -end-0.5 bg-danger text-plaster text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center border-2 border-ink">
                {ops.count > 99 ? "99+" : ops.count}
              </span>
            ) : null}
          </button>
        ) : null}
        <span className={`${can("view_orders") ? "" : "ms-auto "}text-xs text-plaster/70 hidden lg:inline truncate max-w-[26ch]`} data-testid="admin-role-badge">{user.user?.email} · {user.user?.role_label || user.user?.role}</span>
        <button data-testid="admin-logout" className="shrink-0 p-1" onClick={() => { localStorage.removeItem("fl_admin_token"); localStorage.removeItem("fl_token"); setUser(null); }}>
          <LogOut size={16} />
        </button>
      </header>

      <div className="flex">
        <nav className="hidden lg:block w-60 shrink-0 bg-ink text-plaster/80 min-h-[calc(100vh-52px)] py-3 border-e border-black/40 sticky top-0 self-start max-h-screen overflow-y-auto"
             data-testid="admin-sidebar">
          {navTree}
        </nav>

        {navOpen ? (
          <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-nav-drawer" onClick={() => setNavOpen(false)}>
            <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm animate-in fade-in duration-200" />
            <nav className="relative w-[82%] max-w-xs bg-ink text-plaster/80 h-full overflow-y-auto py-3 animate-in slide-in-from-left duration-200"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 pb-2">
                <span className="text-xs text-plaster/60 truncate">{user.user?.email}</span>
                <button type="button" data-testid="admin-nav-close" onClick={() => setNavOpen(false)} className="p-1 text-plaster/70">
                  <X size={18} />
                </button>
              </div>
              {navTree}
            </nav>
          </div>
        ) : null}

        <main className="flex-1 p-4 sm:p-6 min-w-0">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="partners" element={<PartnersPage />} />
            <Route path="city-pages" element={<CityPagesPage />} />
            <Route path="occasion-pages" element={<OccasionPagesPage />} />
            <Route path="discounts" element={<DiscountsPage />} />
            <Route path="extras" element={<ExtrasPage />} />
            <Route path="ops-contacts" element={<OpsContactsPage />} />
            <Route path="newsletter" element={<NewsletterPage />} />
            <Route path="email-templates" element={<EmailTemplatesPage />} />
            <Route path="agentic" element={<AgenticCommercePage />} />
            <Route path="florists" element={<FloristsPage />} />
            <Route path="florists/:floristId" element={<FloristProfile />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:email" element={<CustomerProfile />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="payout-requests" element={<PayoutRequestsPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="delivery-network" element={<DeliveryNetworkPage />} />
            <Route path="catalogue" element={<CataloguePage />} />
            <Route path="moderation" element={<CataloguePage />} />
            <Route path="reviews" element={<ReviewsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="notifications" element={<MessagesPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="marketing" element={<MarketingPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="plans" element={<Navigate to="/admin/partners" replace />} />
            <Route path="addons" element={<AddonsModerationPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="*" element={<Overview />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [booted, setBooted] = useState(() => !localStorage.getItem("fl_admin_token"));
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [mfa, setMfa] = useState(null); // {challenge, method, hint}
  const [enrollForced, setEnrollForced] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("fl_admin_token")) return;
    localStorage.setItem("fl_token", localStorage.getItem("fl_admin_token"));
    api.get("/auth/me")
      .then((r) => { setUser(r.data); if (r.data.user?.mfa?.required && !r.data.user?.mfa?.enabled) setEnrollForced(true); })
      .catch(() => localStorage.removeItem("fl_admin_token"))
      .finally(() => setBooted(true));
  }, []);

  const finish = (data) => {
    if (!data.user.is_admin) { setError("Not an admin account"); return; }
    mfaTrust.save(data);
    localStorage.setItem("fl_token", data.token);
    localStorage.setItem("fl_admin_token", data.token);
    if (data.mfa_enrollment_required) { setUser(data); setEnrollForced(true); return; }
    setUser(data);
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { ...creds, mfa_trust: mfaTrust.get() });
      if (data.mfa_required) {
        setMfa({ challenge: data.challenge_token, method: data.method, hint: data.destination_hint });
        return;
      }
      finish(data);
    } catch (err) {
      setError(errText(err));
    }
  };

  if (enrollForced && user)
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-6" data-testid="admin-mfa-enroll">
        <div className="bg-plaster p-8 w-full max-w-md border border-brass">
          <MfaEnroll allowedMethods={user.user?.mfa?.required ? ["totp", "email", "sms"] : ["totp"]}
                     forced onDone={() => api.get("/auth/me").then((r) => { setUser(r.data); setEnrollForced(false); })} />
        </div>
      </div>
    );

  if (!user && !booted)
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="admin-boot">
        <div className="text-plaster/70 text-sm font-display animate-pulse">Fleurinelle operations</div>
      </div>
    );

  if (!user)
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-6" data-testid="admin-login">
        {mfa ? (
          <div className="bg-plaster p-8 w-full max-w-sm border border-brass">
            <MfaChallenge challenge={mfa.challenge} method={mfa.method} hint={mfa.hint}
                          onVerified={finish} onCancel={() => { setMfa(null); setError(""); }} />
          </div>
        ) : (
          <form onSubmit={login} className="bg-plaster p-8 w-full max-w-sm space-y-4 border border-brass">
            <h1 className="font-display text-2xl text-ink">Fleurinelle operations</h1>
            <input data-testid="admin-email" className="field" placeholder="Email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
            <input data-testid="admin-password" type="password" className="field" placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
            {error ? <p className="text-danger text-sm" data-testid="admin-login-error">{error}</p> : null}
            <button data-testid="admin-login-button" className="btn-primary w-full">Sign in</button>
            <ForgotPasswordLink locale="en" email={creds.email} testid="admin-forgot" />
          </form>
        )}
      </div>
    );

  return <Shell user={user} setUser={setUser} />;
}
